/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type {
	DashboardConfigAndWRMResourceData,
	DashboardAMDModulesResourceData,
	AmdResourceSharedValue,
	DashboardForgeResourceData,
	GadgetDirectoryResourceData,
	DashboardConfigAndWRMResourceDependency,
	GadgetData,
	WebResourceName,
} from '@jira/global-pages__dashboard-common/src/types.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	DASHBOARD_BACKGROUND_SCRIPT_MODULE,
	DASHBOARD_GADGET_MODULE,
	SOURCE_ROUTER,
} from '@jira/forge__forge-ui-constants/src/constants.tsx';
// eslint-disable-next-line jira/import-whitelist
import { fetchModules } from '@jira/forge__forge-ui-utils/src/utils/fetch-modules/doFetchWithGraphQLErrorsTracking.tsx';
import type {
	DashboardReactGadgetsResourceData,
	ReactGadgetsResourceSharedValue,
} from '@jira/global-pages__dashboard-react-gadgets-definition/src/main.tsx';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import {
	createDependentResource,
	withEitherDataOrError,
	type DataOrErrorOrNull,
} from './SharedValueAggregateError.tsx';
import { getKey } from './getKey.tsx';
import getAMDModulesData from './getAMDModulesData.tsx';
import getConfigAndWRMData from './getConfigAndWRMData.tsx';
import getGadgetDirectoryData from './getGadgetDirectoryData.tsx';
import getReactGadgetsData from './getReactGadgetsData.tsx';
import {
	gadgetsToSortedReactGadgets,
	gadgetsToUniqueSortedAmdModules,
} from './gadgetsToUniqueSortedAmdModules.tsx';

const RESOURCE_TYPE_DASHBOARD_CONFIG_AND_WRM = 'DASHBOARD_CONFIG_AND_WRM';
const RESOURCE_TYPE_DASHBOARD_AMD_MODULES = 'DASHBOARD_AMD_MODULES';
const RESOURCE_TYPE_DASHBOARD_REACT_GADGETS = 'DASHBOARD_REACT_GADGETS';
const RESOURCE_TYPE_DASHBOARD_FORGE = 'DASHBOARD_FORGE';
const RESOURCE_TYPE_DASHBOARD_GADGET_DIRECTORY = 'DASHBOARD_GADGET_DIRECTORY';

export const dashboardConfigAndWRMResource: RouteResource<DashboardConfigAndWRMResourceData[]> =
	createResource<DashboardConfigAndWRMResourceData[]>({
		type: RESOURCE_TYPE_DASHBOARD_CONFIG_AND_WRM,
		getKey,
		getData: getConfigAndWRMData,
		maxAge: Infinity,
	});

export const dashboardAMDModulesResource: RouteResource<DashboardAMDModulesResourceData> =
	// eslint-disable-next-line jira/router/no-daisy-chaining-resources
	createDependentResource<
		DashboardAMDModulesResourceData,
		[DashboardConfigAndWRMResourceDependency],
		AmdResourceSharedValue
	>({
		type: RESOURCE_TYPE_DASHBOARD_AMD_MODULES,
		getKey,
		depends: [RESOURCE_TYPE_DASHBOARD_CONFIG_AND_WRM],
		mapToSharedValue: withEitherDataOrError<
			[DashboardConfigAndWRMResourceData[]],
			DataOrErrorOrNull<AmdResourceSharedValue>
		>(
			(
				data: Array<{ gadgets: GadgetData[]; preloadedModuleNames: WebResourceName[] }>,
			): AmdResourceSharedValue => {
				const {
					gadgets,
					preloadedModuleNames,
				}: { gadgets: GadgetData[]; preloadedModuleNames: WebResourceName[] } = data.reduce(
					(prev, curr) => ({
						gadgets: prev.gadgets.concat(curr.gadgets),
						preloadedModuleNames: prev.preloadedModuleNames.concat(curr.preloadedModuleNames),
					}),
					{ gadgets: [], preloadedModuleNames: [] },
				);
				const moduleNames = gadgetsToUniqueSortedAmdModules(gadgets);
				const isPreloaded = moduleNames.every((name) => preloadedModuleNames.includes(name));
				return { moduleNames, isPreloaded };
			},
			(x) => x,
		),
		shouldUpdate: ({ sharedValue: a, amdModules }, b) =>
			withEitherDataOrError<[AmdResourceSharedValue, AmdResourceSharedValue], boolean>(
				(prev, next) =>
					next.moduleNames.length > prev.moduleNames.length ||
					next.moduleNames.some((name) => amdModules?.[name] == null),
				() => true,
			)(a, b),
		getData: getAMDModulesData,
		maxAge: Infinity,
		isBrowserOnly: true,
	});

export const dashboardReactGadgetsResource: RouteResource<DashboardReactGadgetsResourceData> =
	// eslint-disable-next-line jira/router/no-daisy-chaining-resources
	createDependentResource<
		DashboardReactGadgetsResourceData,
		[DashboardConfigAndWRMResourceDependency],
		ReactGadgetsResourceSharedValue
	>({
		type: RESOURCE_TYPE_DASHBOARD_REACT_GADGETS,
		getKey,
		depends: [RESOURCE_TYPE_DASHBOARD_CONFIG_AND_WRM],
		mapToSharedValue: withEitherDataOrError<
			[DashboardConfigAndWRMResourceData[]],
			DataOrErrorOrNull<ReactGadgetsResourceSharedValue>
		>(
			(data): ReactGadgetsResourceSharedValue => {
				const gadgets = data.flatMap((dashboard) => dashboard.gadgets);
				return { reactGadgets: gadgetsToSortedReactGadgets(gadgets) };
			},
			(x) => x,
		),
		shouldUpdate: ({ sharedValue: a, reactGadgetsState }, b) =>
			withEitherDataOrError<
				[ReactGadgetsResourceSharedValue, ReactGadgetsResourceSharedValue],
				boolean
			>(
				(prev, next): boolean =>
					JSON.stringify(next.reactGadgets) !== JSON.stringify(prev.reactGadgets) ||
					(reactGadgetsState != null && Object.values(reactGadgetsState).some((v) => !v)),
				() => true,
			)(a, b),
		getData: getReactGadgetsData,
		maxAge: Infinity,
		isBrowserOnly: true,
	});

export const dashboardForgeResource: RouteResource<DashboardForgeResourceData> =
	createResource<DashboardForgeResourceData>({
		type: RESOURCE_TYPE_DASHBOARD_FORGE,
		getKey,
		getData: async (
			_,
			{ tenantContext: { cloudId, isAnonymous } },
		): Promise<DashboardForgeResourceData> => {
			const modules = await fetchModules({
				cloudId,
				isAnonymous,
				types: [DASHBOARD_GADGET_MODULE, DASHBOARD_BACKGROUND_SCRIPT_MODULE],
				context: {},
				includeHidden: false,
				source: SOURCE_ROUTER,
			});
			return {
				gadgets: modules[DASHBOARD_GADGET_MODULE],
				backgroundScripts: modules[DASHBOARD_BACKGROUND_SCRIPT_MODULE],
			};
		},
		maxAge: Infinity,
	});

export const gadgetDirectoryResource: RouteResource<GadgetDirectoryResourceData | null> =
	createResource<GadgetDirectoryResourceData | null>({
		type: RESOURCE_TYPE_DASHBOARD_GADGET_DIRECTORY,
		getKey: () => 'gadgetDirectoryItems',
		getData: getGadgetDirectoryData,
		maxAge: Infinity,
		isBrowserOnly: true,
	});
